import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiLock, FiEye, FiEyeOff, FiCheck } from "react-icons/fi";
import api from "../../api/axios";
import AuthLayoutWrapper from "../../components/auth/AuthCardWrapper";
import { InputField } from "../../components/FormFields";

export default function ResetPassword() {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [error, setError] = useState({ newPassword: "", confirmPassword: "" });

  const navigate = useNavigate();

  // Retrieve stored reset values from previous screens
  const email = sessionStorage.getItem("reset_email");
  const otp = sessionStorage.getItem("verified_otp");

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError({ newPassword: "", confirmPassword: "" });
    let errorFound = false;

    if (!newPassword.trim()) {
      setError((prev) => ({ ...prev, newPassword: "New password is required" }));
      errorFound = true;
    } else if (newPassword.length < 6) {
      setError((prev) => ({
        ...prev,
        newPassword: "Password must be at least 6 characters",
      }));
      errorFound = true;
    }

    if (!confirmPassword.trim()) {
      setError((prev) => ({
        ...prev,
        confirmPassword: "Please confirm your password",
      }));
      errorFound = true;
    } else if (newPassword !== confirmPassword) {
      setError((prev) => ({
        ...prev,
        confirmPassword: "Passwords do not match",
      }));
      errorFound = true;
    }

    if (errorFound) return;

    if (!email || !otp) {
      setErrorMsg("Session expired. Please restart the verification process.");
      return;
    }

    setErrorMsg("");
    setLoading(true);

    try {
      const response = await api.post("/auth/forgot-password", {
        email,
        otp,
        newPassword,
      });

      if (response.status === 200) {
        sessionStorage.removeItem("reset_email");
        sessionStorage.removeItem("verified_otp");
        navigate("/auth/sign-in");
      }
    } catch (err) {
      setErrorMsg(
        err.response?.data?.message ||
          "Failed to update password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayoutWrapper>
      {/* Header Section */}
      <div className="mb-4 text-center">
        <h3 className="text-3xl font-extrabold text-[#111827]">
          Reset Password
        </h3>
        <p className="mt-1 text-xs font-semibold text-[var( --color-primary)]">
          Create a new password for your account
        </p>
      </div>

      {/* Dynamic API Error Container */}
      {errorMsg ? (
        <div className="mb-3 rounded-lg bg-red-50 p-2.5 text-center text-xs font-medium text-red-600">
          {errorMsg}
        </div>
      ) : null}

      <form
        onSubmit={handleResetPassword}
        className="mb-4 flex flex-1 flex-col justify-between gap-5"
      >
        <div className="space-y-4">
          {/* New Password Field */}
          <InputField
            label="New Password"
            name="newPassword"
            type="password"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              if (error.newPassword)
                setError((prev) => ({ ...prev, newPassword: "" }));
              if (errorMsg) setErrorMsg("");
            }}
            placeholder="Enter new password"
            icon={<FiLock className="text-[#111827]" />}
            error={error.newPassword}

          />

          {/* Confirm Password Field */}
          <InputField
            label="Confirm Password"
            name="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (error.confirmPassword)
                setError((prev) => ({ ...prev, confirmPassword: "" }));
              if (errorMsg) setErrorMsg("");
            }}
            placeholder="Confirm new password"
            icon={<FiLock className="text-[#111827]" />}
            error={error.confirmPassword}
          />
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-[#1A1A1A] py-3 text-xs font-bold tracking-wider text-[#FFFFFF] transition-all hover:bg-[#11172E] active:scale-[0.98] disabled:opacity-50"
        >
          {loading ? "SUBMITTING..." : "SUBMIT"}{" "}
          <FiCheck className="text-sm" />
        </button>
      </form>
    </AuthLayoutWrapper>
  );
}